export type BoxSize = { w: number; h: number };
export type Range = { l: number; t: number; r: number; b: number };
export type Point = { x: number; y: number };

/** クロップ枠(frame)のサイズ。canvas内に収まる、目標比率(ratio = height/width)の最大サイズの矩形を中央配置で返す。
 * frameはズーム・ドラッグ操作によらず常に一定(固定)であり、この関数は画像読み込み時に一度だけ呼べばよい */
export function computeFrame(canvasSize: BoxSize, ratio: number): Range {
  const { w: cw, h: ch } = canvasSize;
  const fitsWidth = ch / cw >= ratio;
  const fw = fitsWidth ? cw : ch / ratio;
  const fh = fitsWidth ? cw * ratio : ch;
  const l = cw / 2 - fw / 2;
  const t = ch / 2 - fh / 2;
  return { l, t, r: l + fw, b: t + fh };
}

/** 画像がframe全体を覆いきる最小の拡大率。ズームスライダー0%(最もズームアウトした状態)に対応する */
export function computeMinScale(frame: Range, imgWidth: number, imgHeight: number): number {
  const frameW = frame.r - frame.l;
  const frameH = frame.b - frame.t;
  return Math.max(frameW / imgWidth, frameH / imgHeight);
}

/** ズームスライダーの値(0〜1)から拡大率を計算する。0=minScale(frameにフィット)、1=maxScale */
export function computeScaleForT(minScale: number, maxScale: number, t: number): number {
  return minScale + t * (maxScale - minScale);
}

/** 拡大率からズームスライダーの値(0〜1)を逆算する。ホイールズーム後にスライダー表示を同期するために使う */
export function computeTForScale(minScale: number, maxScale: number, scale: number): number {
  if (maxScale <= minScale) return 0;
  return Math.min(1, Math.max(0, (scale - minScale) / (maxScale - minScale)));
}

/** 画像がframeを覆い続けるよう、画像中心位置(center)をclampする(拡大率は変えない) */
export function clampImageCenter(
  center: Point,
  scale: number,
  imgWidth: number,
  imgHeight: number,
  frame: Range
): Point {
  const w = imgWidth * scale;
  const h = imgHeight * scale;

  // 画像の左端がframe.l以下、右端がframe.r以上になる中心x座標の範囲(minScale前提であれば必ずminX<=maxX)
  const minX = frame.r - w / 2;
  const maxX = frame.l + w / 2;
  const minY = frame.b - h / 2;
  const maxY = frame.t + h / 2;

  const x = minX <= maxX ? Math.min(maxX, Math.max(minX, center.x)) : (frame.l + frame.r) / 2;
  const y = minY <= maxY ? Math.min(maxY, Math.max(minY, center.y)) : (frame.t + frame.b) / 2;

  return { x, y };
}

/**
 * 拡大率をoldScaleからnewScaleに変更する際、canvas上のanchor地点の直下にある画像上の点が
 * 画面上で動かないよう画像中心位置を再計算する(スライダーはframe中心、ホイールはカーソル位置をanchorに使う)。
 * frame外に出ないよう最後にclampImageCenterを適用するため、clamp発生時はanchor位置がわずかにずれ得る
 */
export function zoomAtPoint(
  center: Point,
  oldScale: number,
  newScale: number,
  anchor: Point,
  imgWidth: number,
  imgHeight: number,
  frame: Range
): Point {
  const oldLeft = center.x - (imgWidth * oldScale) / 2;
  const oldTop = center.y - (imgHeight * oldScale) / 2;

  // anchorが画像のどの位置(0〜1の比率、拡大率に依存しない)に当たるかを求める
  const u = (anchor.x - oldLeft) / (imgWidth * oldScale);
  const v = (anchor.y - oldTop) / (imgHeight * oldScale);

  const newLeft = anchor.x - u * imgWidth * newScale;
  const newTop = anchor.y - v * imgHeight * newScale;
  const newCenter = { x: newLeft + (imgWidth * newScale) / 2, y: newTop + (imgHeight * newScale) / 2 };

  return clampImageCenter(newCenter, newScale, imgWidth, imgHeight, frame);
}
